package com.castly.admin.ui.audiobooks

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AdminEpisodeDetailDto
import com.castly.shared.dto.EpisodeTranslationDto
import com.castly.shared.dto.UpsertEpisodeTranslationRequest
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * EpisodeTranslationEditor — tab switcher for ENG/ARA episode translations.
 * Shows a completeness badge per tab and Save button per tab.
 */
@Composable
fun EpisodeTranslationEditor(
    episode: AdminEpisodeDetailDto,
    apiClient: ApiClient,
    onTranslationSaved: () -> Unit
) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()

    var activeTab by remember { mutableStateOf("eng") }
    var engTitle by remember(episode.id) { mutableStateOf(episode.translations["eng"]?.title ?: episode.title) }
    var engDescription by remember(episode.id) { mutableStateOf(episode.translations["eng"]?.description ?: episode.description ?: "") }
    var araTitle by remember(episode.id) { mutableStateOf(episode.translations["ara"]?.title ?: "") }
    var araDescription by remember(episode.id) { mutableStateOf(episode.translations["ara"]?.description ?: "") }

    var isSaving by remember { mutableStateOf(false) }
    var saveError by remember { mutableStateOf<String?>(null) }
    var saveSuccess by remember { mutableStateOf(false) }

    fun completeness(title: String, description: String): String = when {
        title.isNotBlank() && description.isNotBlank() -> "✓"
        title.isNotBlank() || description.isNotBlank() -> "⚠"
        else -> "✗"
    }

    fun badgeColor(badge: String): String = when (badge) {
        "✓" -> "#16a34a"
        "⚠" -> "#d97706"
        else -> "#dc2626"
    }

    LaunchedEffect(saveSuccess) {
        if (saveSuccess) {
            kotlinx.coroutines.delay(2000)
            saveSuccess = false
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(Spacing.md)
        }
    }) {
        H3({
            style {
                fontSize(Typography.lg)
                fontWeight("600")
                color(Color(colors.text))
                margin(0.px)
            }
        }) { Text("Translations") }

        // Tab switcher
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(Spacing.sm)
                property("border-bottom", "1px solid ${colors.border}")
            }
        }) {
            listOf("eng" to "English", "ara" to "Arabic").forEach { (lang, label) ->
                val badge = if (lang == "eng") completeness(engTitle, engDescription)
                            else completeness(araTitle, araDescription)
                Button({
                    onClick { activeTab = lang }
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        border(0.px)
                        property("border-bottom", "${if (activeTab == lang) "2px" else "0px"} solid ${colors.primary}")
                        backgroundColor(Color("transparent"))
                        cursor("pointer")
                        fontSize(Typography.base)
                        fontWeight(if (activeTab == lang) "600" else "400")
                        color(Color(if (activeTab == lang) colors.primary else colors.text))
                    }
                }) {
                    Text("$label ")
                    Span({
                        style {
                            fontSize(Typography.xs)
                            color(Color(badgeColor(badge)))
                            fontWeight("700")
                        }
                    }) { Text(badge) }
                }
            }
        }

        // Tab content
        val (currentTitle, setTitle, currentDescription, setDescription, currentLang) = if (activeTab == "eng") {
            listOf<Any>(engTitle, { s: String -> engTitle = s }, engDescription, { s: String -> engDescription = s }, "eng")
        } else {
            listOf<Any>(araTitle, { s: String -> araTitle = s }, araDescription, { s: String -> araDescription = s }, "ara")
        }

        @Suppress("UNCHECKED_CAST")
        val titleValue = currentTitle as String
        @Suppress("UNCHECKED_CAST")
        val titleSetter = setTitle as (String) -> Unit
        @Suppress("UNCHECKED_CAST")
        val descValue = currentDescription as String
        @Suppress("UNCHECKED_CAST")
        val descSetter = setDescription as (String) -> Unit
        val lang = currentLang as String

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(Spacing.md)
            }
        }) {
            // Title field
            Div({
                style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(Spacing.xs) }
            }) {
                Label(attrs = {
                    style { fontSize(Typography.sm); fontWeight("600"); color(Color(colors.textMuted)) }
                }) { Text("Title") }
                Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
                    value(titleValue)
                    onInput { titleSetter(it.value) }
                    placeholder("Chapter title")
                    style {
                        width(100.percent)
                        padding(Spacing.sm, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        property("box-sizing", "border-box")
                        themeTransition()
                    }
                }
            }

            // Description field
            Div({
                style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(Spacing.xs) }
            }) {
                Label(attrs = {
                    style { fontSize(Typography.sm); fontWeight("600"); color(Color(colors.textMuted)) }
                }) { Text("Description") }
                TextArea(attrs = {
                    value(descValue)
                    onInput { descSetter(it.value) }
                    placeholder("Chapter description")
                    style {
                        width(100.percent)
                        height(80.px)
                        padding(Spacing.sm, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        property("box-sizing", "border-box")
                        property("resize", "vertical")
                        themeTransition()
                    }
                })
            }

            saveError?.let {
                Div({
                    style {
                        fontSize(Typography.sm)
                        color(Color(colors.danger))
                        backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                        padding(Spacing.sm)
                        borderRadius(BorderRadius.sm)
                    }
                }) { Text(it) }
            }

            if (saveSuccess) {
                Div({
                    style {
                        fontSize(Typography.sm)
                        color(Color("#16a34a"))
                        backgroundColor(Color("#dcfce7"))
                        padding(Spacing.sm)
                        borderRadius(BorderRadius.sm)
                    }
                }) { Text("Translation saved") }
            }

            Button({
                onClick {
                    scope.launch {
                        isSaving = true
                        saveError = null
                        try {
                            apiClient.upsertEpisodeTranslation(
                                audiobookId = episode.audiobookIsbn,
                                episodeId = episode.id,
                                lang = lang,
                                request = UpsertEpisodeTranslationRequest(
                                    title = titleValue.takeIf { it.isNotBlank() },
                                    description = descValue.takeIf { it.isNotBlank() }
                                )
                            )
                            saveSuccess = true
                            onTranslationSaved()
                        } catch (e: Exception) {
                            saveError = e.message ?: "Failed to save translation"
                        } finally {
                            isSaving = false
                        }
                    }
                }
                if (isSaving) disabled()
                style {
                    alignSelf("flex-start")
                    padding(Spacing.sm, Spacing.lg)
                    fontSize(Typography.base)
                    fontWeight("600")
                    color(Color("#ffffff"))
                    backgroundColor(Color(if (isSaving) colors.textMuted else colors.primary))
                    border(0.px)
                    borderRadius(BorderRadius.md)
                    cursor(if (isSaving) "not-allowed" else "pointer")
                }
            }) { Text(if (isSaving) "Saving..." else "Save ${if (lang == "eng") "English" else "Arabic"}") }
        }
    }
}
